import React, { useState, useEffect, useRef } from 'react';
import {
  makeStyles,
  Paper,
  Button,
  Typography,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Box,
  TextField,
  Chip,
  useTheme,
  useMediaQuery
} from '@material-ui/core';
import { 
  MuiPickersUtilsProvider, 
  KeyboardDatePicker 
} from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import moment from 'moment';
import 'moment/locale/es';
import { i18n } from "../../translate/i18n";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  filterContainer: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(1.5),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  formControl: {
    width: '100%',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(1.5),
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1),
    },
  },
  inputRoot: {
    '&.MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: theme.palette.background.paper,
      '& fieldset': {
        borderColor: theme.palette.divider,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.divider,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.divider,
      },
    }
  },
  selectField: {
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
    },
    '& .MuiSelect-select:focus': {
      backgroundColor: 'transparent',
    },
  },
  selectRoot: {
    padding: '10.5px 14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    [theme.breakpoints.down('md')]: {
      padding: '8px 12px',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '6px 10px',
    },
  },
  datePicker: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: theme.palette.background,
      '& fieldset': {
        borderColor: theme.palette.divider,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.divider,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.divider,
      },
    },
    '& .MuiInputLabel-outlined': {
      transform: 'translate(14px, 16px) scale(1)',
      color: theme.palette.text,
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
  },
  dateContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(1.5),
    },
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1),
      flexDirection: 'column',
    },
  },
  dateFieldContainer: {
    flex: 1,
  },
  filterComponent: {
    marginBottom: theme.spacing(2),
    '& > div': {
      padding: 0,
    },
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(1.5),
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1),
      flexDirection: 'column',
      gap: theme.spacing(0.8),
    },
  },
  applyButton: {
    borderRadius: 8,
    padding: theme.spacing(1, 3),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(0.8, 2.5),
      fontSize: '0.875rem',
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.6, 2),
      fontSize: '0.75rem',
    },
  },
  clearButton: {
    borderRadius: 8,
    padding: theme.spacing(1, 3),
    borderColor: theme.palette.divider,
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(0.8, 2.5),
      fontSize: '0.875rem',
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.6, 2),
      fontSize: '0.75rem',
    },
  },
  dateSectionTitle: {
    fontSize: '0.875rem',
    fontWeight: 400,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
    [theme.breakpoints.down('md')]: {
      fontSize: '0.8rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.75rem',
      marginBottom: theme.spacing(0.3),
    },
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    maxHeight: '100px',
    overflowY: 'auto',
    [theme.breakpoints.down('sm')]: {
      maxHeight: '80px',
    },
  },
  chip: {
    margin: 2,
    [theme.breakpoints.down('md')]: {
      margin: 1,
      fontSize: '0.75rem',
      height: 24,
    },
    [theme.breakpoints.down('sm')]: {
      margin: 1,
      fontSize: '0.7rem',
      height: 20,
    },
  },
}));

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

// MenuProps responsive - se ajustará según el tamaño de pantalla
const getMenuProps = (isMobile = false) => ({
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * (isMobile ? 3.5 : 4.5) + ITEM_PADDING_TOP,
      width: isMobile ? 200 : 250,
      fontSize: isMobile ? '0.75rem' : '1rem',
    },
  },
  anchorOrigin: {
    vertical: "bottom",
    horizontal: "left"
  },
  transformOrigin: {
    vertical: "top",
    horizontal: "left"
  },
  getContentAnchorEl: null
});

// MenuProps por defecto (escritorio)
const MenuProps = getMenuProps(false);

const TicketsFilters = ({ 
  onFilterChange, 
  showStatusFilter = false,
  onTagsChange,
  onUsersChange,
  isAdmin,
  companyId,
  forceReloadTickets
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [selectedConnectionIds, setSelectedConnectionIds] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [dateError, setDateError] = useState('');
  const [queues, setQueues] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allConnections, setAllConnections] = useState([]);
  
  useEffect(() => {
    const fetchQueues = async () => {
      try {
        console.log("Iniciando carga de departamentos...");
        // Si es admin, obtener todos los departamentos
        const { data } = await api.get("/queue/user");        
        if (data && Array.isArray(data)) {
          setQueues(data);
        }
      } catch (err) {
        console.error("TicketsFilters - Error al cargar departamentos:", err);
        setQueues([]);
      }
    };
    
    fetchQueues();
  }, [companyId, isAdmin]);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const { data } = await api.get("/tags", {
          params: { companyId }
        });
        
        if (data && data.tags && Array.isArray(data.tags)) {
          setAllTags(data.tags);
        } else if (Array.isArray(data)) {
          setAllTags(data);
        } else {
          setAllTags([]);
        }
      } catch (err) {
        console.error("TicketsFilters - Error al cargar etiquetas:", err);
        setAllTags([]);
      }
    };
    
    fetchTags();
  }, [companyId]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/list", {
          params: { companyId }
        });
        
        if (data && data.users && Array.isArray(data.users)) {
          setAllUsers(data.users);
        } else if (Array.isArray(data)) {
          setAllUsers(data);
        } else {
          setAllUsers([]);
        }
      } catch (err) {
        console.error("TicketsFilters - Error al cargar usuarios:", err);
        setAllUsers([]);
      }
    };
    
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin, companyId]);

  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const { data } = await api.get(`/whatsapp?session=0&companyId=${companyId}`, {
          params: { companyId }
        });
        
        if (data && Array.isArray(data)) {
          setAllConnections(data);
        } else {
          setAllConnections([]);
        }
      } catch (err) {
        console.error("TicketsFilters - Error al cargar conexiones:", err);
        setAllConnections([]);
      }
    };
    
    fetchConnections();
  }, [companyId]);

  const validateDates = (fromDate, toDate) => {
    if (fromDate && toDate && moment(fromDate).isAfter(moment(toDate))) {
      setDateError('La fecha desde no puede ser mayor que la fecha hasta');
      return false;
    }
    setDateError('');
    return true;
  };

  const handleApplyFilters = () => {
    
    // Validar fechas antes de aplicar filtros
    if (!validateDates(dateFrom, dateTo)) {
      return;
    }
    
    const cleanedStatus = selectedStatus || '';
    const cleanedDateFrom = dateFrom ? moment(dateFrom).format('YYYY-MM-DD') : '';
    const cleanedDateTo = dateTo ? moment(dateTo).format('YYYY-MM-DD') : '';
    
    const filtersForUI = {
      tags: selectedTagIds,
      users: selectedUserIds,
      queueIds: selectedQueueIds.length === 1 ? selectedQueueIds[0] : selectedQueueIds,
      connections: selectedConnectionIds,
      status: cleanedStatus,
      dateFrom: cleanedDateFrom,
      dateTo: cleanedDateTo
    };
    
    if (typeof onFilterChange === 'function') {
      onFilterChange(filtersForUI);
    }
    
  };

  const handleClearFilters = () => {
    
    // Resetear todos los estados locales
    setSelectedTagIds([]);
    setSelectedUserIds([]);
    setSelectedQueueIds([]);
    setSelectedConnectionIds([]);
    setSelectedStatus('');
    setDateFrom(null);
    setDateTo(null);
    setDateError('');
    
    // Enviar filtros vacíos al componente padre
    const clearedFilters = {
      tags: [],
      users: [],
      queueIds: [],
      connections: [],
      status: '',
      dateFrom: '',
      dateTo: ''
    };
    
    if (typeof onFilterChange === 'function') {
      onFilterChange(clearedFilters);
    }
    
  };

  const handleQueueChange = (event) => {
    const selectedIds = event.target.value;
    
    setSelectedQueueIds(selectedIds);
  };

  const handleChangeTags = (event) => {
    const selectedIds = event.target.value;
    setSelectedTagIds(selectedIds);
  };

  const handleChangeUser = (event) => {
    const selectedIds = event.target.value;
    
    setSelectedUserIds(selectedIds);
  };

  const handleChangeConnection = (event) => {
    const selectedIds = event.target.value;
    
    setSelectedConnectionIds(selectedIds);
    
  };

const renderQueueSelect = () => (
  <FormControl variant="outlined" className={classes.formControl}>
    <Typography variant="body2" className={classes.dateSectionTitle}>
      Filtro por departamento
    </Typography>
    <Select
      multiple
      value={selectedQueueIds} // Usar IDs directamente
      onChange={handleQueueChange}
      displayEmpty
      className={classes.selectField}
      classes={{ root: classes.selectRoot }}
      renderValue={(selectedIds) => (
        <div className={classes.chips}>
          {selectedIds.length === 0 ? (
            <em>Seleccionar departamentos</em>
          ) : (
            selectedIds.map((id) => {
              const queue = queues.find(q => q.id === id);
              return (
                <Chip
                  key={id}
                  label={queue?.name || ''}
                  className={classes.chip}
                  style={{ 
                    backgroundColor: queue?.color || '#3f51b5', 
                    color: 'white' 
                  }}
                  onDelete={(event) => {
                    event.stopPropagation();
                    const newIds = selectedQueueIds.filter(selectedId => selectedId !== id);
                    setSelectedQueueIds(newIds);
                  }}
                  onMouseDown={(event) => event.stopPropagation()}
                />
              );
            })
          )}
        </div>
      )}
      MenuProps={getMenuProps(isMobile)}
    >
      <MenuItem disabled value="">
        <em>Seleccionar departamentos</em>
      </MenuItem>
      {queues.map((queue) => (
        <MenuItem key={queue.id} value={queue.id}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ 
              width: 16, 
              height: 16, 
              borderRadius: 4, 
              backgroundColor: queue.color || '#3f51b5'
            }} />
            {queue.name}
          </div>
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

  const renderTagsSelect = () => (
    <FormControl variant="outlined" className={classes.formControl}>
      <Typography variant="body2" className={classes.dateSectionTitle}>
        Filtro por etiquetas
      </Typography>
      <Select
        multiple
        value={selectedTagIds} // Usar IDs directamente
        onChange={handleChangeTags}
        displayEmpty
        className={classes.selectField}
        classes={{ root: classes.selectRoot }}
        renderValue={(selectedIds) => (
          <div className={classes.chips}>
            {selectedIds.length === 0 ? (
              <em>Seleccionar etiquetas</em>
            ) : (
              selectedIds.map((id) => {
                const tag = allTags.find(t => t.id === id);
                return (
                  <Chip
                    key={id}
                    label={tag?.name || ''}
                    className={classes.chip}
                    style={{ 
                      backgroundColor: tag?.color || '#cccccc', 
                      color: 'white' 
                    }}
                    onDelete={(event) => {
                      event.stopPropagation();
                      const newIds = selectedTagIds.filter(selectedId => selectedId !== id);
                      setSelectedTagIds(newIds);
                    }}
                    onMouseDown={(event) => event.stopPropagation()}
                  />
                );
              })
            )}
          </div>
        )}
        MenuProps={getMenuProps(isMobile)}
      >
        <MenuItem disabled value="">
          <em>Seleccionar etiquetas</em>
        </MenuItem>
        {allTags.map((tag) => (
          <MenuItem key={tag.id} value={tag.id}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ 
                width: 16, 
                height: 16, 
                borderRadius: 4, 
                backgroundColor: tag.color || '#cccccc'
              }} />
              {tag.name}
            </div>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderUsersSelect = () => {
    if (!isAdmin) return null;
    
    return (
      <FormControl variant="outlined" className={classes.formControl}>
        <Typography variant="body2" className={classes.dateSectionTitle}>
          Filtro por usuarios
        </Typography>
        <Select
          multiple
          value={selectedUserIds} // Usar IDs directamente
          onChange={handleChangeUser}
          displayEmpty
          className={classes.selectField}
          classes={{ root: classes.selectRoot }}
          renderValue={(selectedIds) => (
            <div className={classes.chips}>
              {selectedIds.length === 0 ? (
                <em>Seleccionar usuarios</em>
              ) : (
                selectedIds.map((id) => {
                  const user = allUsers.find(u => u.id === id);
                  return (
                    <Chip
                      key={id}
                      label={user?.name || ''}
                      className={classes.chip}
                      onDelete={(event) => {
                        event.stopPropagation();
                        const newIds = selectedUserIds.filter(selectedId => selectedId !== id);
                        setSelectedUserIds(newIds);
                      }}
                      onMouseDown={(event) => event.stopPropagation()}
                    />
                  );
                })
              )}
            </div>
          )}
          MenuProps={getMenuProps(isMobile)}
        >
          <MenuItem disabled value="">
            <em>Seleccionar usuarios</em>
          </MenuItem>
          {allUsers.map((user) => (
            <MenuItem key={user.id} value={user.id}>
              {user.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const renderConnectionsSelect = () => (
    <FormControl variant="outlined" className={classes.formControl}>
      <Typography variant="body2" className={classes.dateSectionTitle}>
        Filtro por conexión
      </Typography>
      <Select
        multiple
        value={selectedConnectionIds}
        onChange={handleChangeConnection}
        displayEmpty
        className={classes.selectField}
        classes={{ root: classes.selectRoot }}
        renderValue={(selectedIds) => (
          <div className={classes.chips}>
            {selectedIds.length === 0 ? (
              <em>Seleccionar conexiones</em>
            ) : (
              selectedIds.map((id) => {
                const connection = allConnections.find(c => c.id === id);
                return (
                  <Chip
                    key={id}
                    label={connection?.name || ''}
                    className={classes.chip}
                    style={{
                      backgroundColor: connection?.status === 'CONNECTED' ? '#4caf50' : '#f44336',
                      color: 'white'
                    }}
                    onDelete={(event) => {
                      event.stopPropagation();
                      const newIds = selectedConnectionIds.filter(selectedId => selectedId !== id);
                      setSelectedConnectionIds(newIds);
                    }}
                    onMouseDown={(event) => event.stopPropagation()}
                  />
                );
              })
            )}
          </div>
        )}
        MenuProps={getMenuProps(isMobile)}
      >
        <MenuItem disabled value="">
          <em>Seleccionar conexiones</em>
        </MenuItem>
        {allConnections.map((connection) => (
          <MenuItem key={connection.id} value={connection.id}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: connection.status === 'CONNECTED' ? '#4caf50' : '#f44336'
              }} />
              {connection.name}
              <span style={{ fontSize: '0.75rem', color: '#666', marginLeft: 'auto' }}>
                ({connection.status})
              </span>
            </div>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderStatusSelect = () => {
    if (!showStatusFilter) return null;
    
    return (
      <FormControl variant="outlined" className={classes.formControl}>
        <Typography variant="body2" className={classes.dateSectionTitle}>
          {i18n.t("tickets.filters.status")}
        </Typography>
        <Select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          displayEmpty
          className={classes.selectField}
          classes={{
            root: classes.selectRoot
          }}
          MenuProps={getMenuProps(isMobile)}
        >
          <MenuItem value="">
            <em>{i18n.t("tickets.filters.allStatus")}</em>
          </MenuItem>
          <MenuItem value="open">{i18n.t("tickets.filters.open")}</MenuItem>
          <MenuItem value="pending">{i18n.t("tickets.filters.pending")}</MenuItem>
          <MenuItem value="closed">{i18n.t("tickets.filters.closed")}</MenuItem>
        </Select>
      </FormControl>
    );
  };

  const renderDatePickers = () => (
    <MuiPickersUtilsProvider utils={MomentUtils} locale="es">
      <div className={classes.dateContainer}>
        <div className={classes.dateFieldContainer}>
          <Typography variant="body2" className={classes.dateSectionTitle}>
            Desde
          </Typography>
          <KeyboardDatePicker
            variant="inline"
            inputVariant="outlined"
            format="DD/MM/YYYY"
            value={dateFrom}
            onChange={(date) => {
              console.log("Fecha desde seleccionada:", date ? date.format('YYYY-MM-DD') : null);
              setDateFrom(date);
              validateDates(date, dateTo);
            }}
            KeyboardButtonProps={{
              "aria-label": "cambiar fecha"
            }}
            className={classes.datePicker}
            placeholder="DD/MM/AAAA"
            autoOk
            disableToolbar
            error={!!dateError}
          />
        </div>

        <div className={classes.dateFieldContainer}>
          <Typography variant="body2" className={classes.dateSectionTitle}>
            Hasta
          </Typography>
          <KeyboardDatePicker
            variant="inline"
            inputVariant="outlined"
            format="DD/MM/YYYY"
            value={dateTo}
            onChange={(date) => {
              console.log("Fecha hasta seleccionada:", date ? date.format('YYYY-MM-DD') : null);
              setDateTo(date);
              validateDates(dateFrom, date);
            }}
            KeyboardButtonProps={{
              "aria-label": "cambiar fecha"
            }}
            className={classes.datePicker}
            placeholder="DD/MM/AAAA"
            error={!!dateError}
            autoOk
            disableToolbar
          />
        </div>
      </div>
      {dateError && (
        <Typography 
          variant="caption" 
          style={{ 
            color: '#f44336', 
            marginTop: '8px', 
            display: 'block',
            textAlign: 'center'
          }}
        >
          {dateError}
        </Typography>
      )}
    </MuiPickersUtilsProvider>
  );

  return (
    <Paper className={classes.filterContainer} elevation={0}>
      {renderQueueSelect()}
      {renderTagsSelect()}
      {renderUsersSelect()}
      {renderConnectionsSelect()}
      {renderStatusSelect()}
      {renderDatePickers()}

      <div className={classes.buttonContainer}>
        <Button
          variant="outlined"
          onClick={handleClearFilters}
          className={classes.clearButton}
        >
          {i18n.t("tickets.buttons.clear")}
        </Button>
        <Button
          variant="contained"
          onClick={handleApplyFilters}
          className={classes.applyButton}
        >
          {i18n.t("tickets.buttons.apply")}
        </Button>
      </div>
    </Paper>
  );
};

export default TicketsFilters;